// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useAccountForm } from '@client/banks/hooks/use-account-form';
import type { BankAccount } from '@client/banks/types/account';
import {
  createEmptyBankAccount,
  getDefaultBankCurrency,
} from '@client/banks/utils/bank-form-defaults';
import { useBankUploadData } from '@client/banks/providers/bank-upload-provider';
import { useStepsData } from '@shared/ui-kit/hooks/use-steps';
import { useCallback, useMemo } from 'react';

export const useAccountsForm = () => {
  const { navigate } = useStepsData();
  const {
    formData,
    listEditor,
    currencyOptions,
    commitListEditor,
    closeListEditor,
  } = useBankUploadData();

  const defaultValues = useMemo(() => {
    const emptyAccount = createEmptyBankAccount(
      getDefaultBankCurrency(currencyOptions),
    );

    if (listEditor?.section !== 'accounts') {
      return emptyAccount;
    }

    if (listEditor.index < formData.accounts.length) {
      const account = formData.accounts[listEditor.index];

      return {
        ...account,
        owners: account.owners.map(owner => ({ ...owner })),
      };
    }

    return emptyAccount;
  }, [currencyOptions, formData.accounts, listEditor]);

  const form = useAccountForm({
    variant: 'create',
    defaultValues,
  });

  const isFormValid = useCallback(
    () => form.formState.isValid,
    [form.formState.isValid],
  );

  const onSubmit = useCallback(
    (data: BankAccount) => {
      commitListEditor(data);
      navigate('accounts-list');
    },
    [commitListEditor, navigate],
  );

  const onCancel = useCallback(() => {
    closeListEditor();
    navigate('accounts-list');
  }, [closeListEditor, navigate]);

  return {
    form,
    currencyOptions,
    isFormValid,
    onSubmit,
    onCancel,
  };
};
